import * as React from 'react';
import { debounce, isEqual, uniqBy } from 'lodash';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import type { Data, MixedSelectDropdownItem } from 'types';

type Props = {
  dataFetcher: (searchTerm: string) => Promise<Array<MixedSelectDropdownItem>>,
  maxItems?: number,
  placeholder?: string,
  data?: Data,
  items?: Array<MixedSelectDropdownItem>,
  withRandomizedAutocomplete?: boolean,
  withMulti?: boolean,
  withSearchable?: boolean,
  withCloseOnSelect?: boolean,
  withSavedInput?: boolean,
  shouldUpdateItems?: boolean,
  withTruncate?: boolean,
  onItemsUpdate?: (items: Array<MixedSelectDropdownItem>, data?: Data | null) => void
};

type State = {
  filteredResults: Array<MixedSelectDropdownItem>,
  selectedItems: Array<MixedSelectDropdownItem>
};

class SearchableDropdown extends React.PureComponent<Props, State> {
  static defaultProps = {
    withMulti: true,
    withSearchable: true
  };

  state = {
    filteredResults: [],
    selectedItems: this.props.items || []
  };

  constructor(props: Props) {
    super(props);

    this.debouncedSearch = debounce(this.handleSearch, 500);
  }

  componentDidUpdate() {
    const { items, shouldUpdateItems } = this.props;
    const { selectedItems } = this.state;

    if (!isEqual(items, selectedItems) && shouldUpdateItems) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(prevState => ({
        ...prevState,
        selectedItems: items || []
      }));
    }
  }

  debouncedSearch: (searchTerm: string) => void = () => {};

  handleItemsUpdate = (updatedItems: Array<MixedSelectDropdownItem>, data?: Data | null): void => {
    const { withSavedInput } = this.props;

    this.setState((prevState) => ({
      selectedItems: updatedItems.filter((item) => item.isSelected),
      filteredResults: withSavedInput ? prevState.filteredResults : []
    }));

    const { onItemsUpdate } = this.props;
    if (onItemsUpdate) {
      onItemsUpdate(updatedItems, data);
    }
  };

  handleSearch = (searchTerm: string): void => {
    if (!searchTerm) {
      // clean immediately
      this.setState({
        filteredResults: []
      });
      return;
    }

    const foundData = this.props.dataFetcher(searchTerm);
    foundData.then(result => {
      this.setState({
        filteredResults: result
      });
    });
  };

  render(): React.ReactNode {
    const {
      data,
      maxItems,
      placeholder,
      withRandomizedAutocomplete,
      withMulti,
      withSearchable,
      withCloseOnSelect,
      withSavedInput,
      withTruncate
    } = this.props;

    const { filteredResults, selectedItems } = this.state;
    const items = uniqBy(selectedItems.concat(filteredResults), 'identifier');

    return (
      <MixedSelectDropdown
        name="discovery-creators-filters-content"
        data={data}
        items={items}
        withMulti={withMulti}
        withSearchable={withSearchable}
        withRemovable
        withDynamicData
        withTruncate={withTruncate}
        withCloseOnSelect={withCloseOnSelect}
        withSavedInput={withSavedInput}
        withRandomizedAutocomplete={withRandomizedAutocomplete}
        hideChevron
        maxItems={maxItems}
        placeholder={placeholder}
        onItemsToggle={this.handleItemsUpdate}
        onItemsRemove={this.handleItemsUpdate}
        onItemsRemoveAll={this.handleItemsUpdate}
        onItemsSearch={this.debouncedSearch}
      />
    );
  }
}

export default SearchableDropdown;
